// core
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

// ui primitives
import { Button, Card, CircularProgress, Stack, Typography } from "Components/UI";

// services
import { useAuthenticate as authenticate } from "Services/Authentication";
import {
    confirmBreakGlassTotp,
    redeemBreakGlassLink,
    startBreakGlassTotp,
    verifyBreakGlassTotp,
} from "Services/BreakGlassAPI";

// components
import LoginLogo from "./LoginLogo";
import OtpInput from "./OtpInput";

// styles
import styles from "./BreakGlass.module.css";

const STEP = {
    REDEEMING: "redeeming",
    ENROLL: "enroll",
    VERIFY: "verify",
    DEAD: "dead",
};

/**
 * The landing page for an emailed emergency sign-in link.
 *
 * Two things here are load-bearing and easy to break:
 *
 * 1. **The GET must not consume the token.** Mail security gateways prefetch
 *    links, so the token is redeemed by an explicit POST from this page, once.
 *    The `redeemedRef` guard matters in development too, where React 18
 *    StrictMode double-invokes effects — without it the second invocation burns
 *    the link and the admin sees "no longer valid" on a link they just clicked.
 * 2. **`totp/start` must be called once.** Calling it again issues a *different*
 *    secret and silently invalidates the one already scanned.
 */
export default function BreakGlassLoginForm() {
    // injections
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // own state
    const [step, setStep] = useState(STEP.REDEEMING);
    const [challenge, setChallenge] = useState("");
    const [email, setEmail] = useState("");
    const [enrollment, setEnrollment] = useState(null);
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // Redemption is single-use, so it must survive a StrictMode double-invoke.
    const redeemedRef = useRef(false);

    const token = searchParams.get("token") || "";

    function completeLogin(response) {
        // Carry the marker into the stored profile so the console can hide what an
        // emergency session must not be offered. get_profile_data re-reports it per
        // request, so a refresh does not lose it.
        authenticate({ ...response.user, break_glass: true }, response.tenant);

        // Land exactly where every other form here lands. Deliberately NOT
        // `response.page`: the backend computes that key but nothing routes on it,
        // its paths are absolute and mostly are not children of /app, and /app's
        // catch-all renders AuthenticatedErrorPage rather than redirecting — so
        // routing on it drops the emergency admin on an error screen. The only
        // load-bearing `page` value is the "login-mfa" sentinel, which break-glass
        // never receives.
        completeRedirect(response.tenant.landing_page);
    }

    function completeRedirect(LandingPage) {
        const redirectPath = localStorage.getItem("redirectPath") || "/";
        localStorage.removeItem("redirectPath"); // Clear the saved path
        if (redirectPath === "/") {
            window.location.href = LandingPage
                ? `/unixi-management-portal/app/${LandingPage}`
                : "/unixi-management-portal/app/dashboard";
        } else if (redirectPath.startsWith("/unixi-management-portal")) {
            window.location.href = `${redirectPath}`;
        } else {
            window.location.href = `/unixi-management-portal${redirectPath}`;
        }
    }

    const beginEnrollment = useCallback(async (challengeToken) => {
        const response = await startBreakGlassTotp(challengeToken);
        if (!response?.status) {
            setError(response?.info || "Unable to start authenticator setup.");
            setStep(STEP.DEAD);
            return;
        }
        setEnrollment({
            secret: response.secret,
            provisioningUri: response.provisioning_uri,
        });
        setStep(STEP.ENROLL);
    }, []);

    useEffect(() => {
        if (redeemedRef.current) return;
        redeemedRef.current = true;

        if (!token) {
            setError("This sign-in link is no longer valid.");
            setStep(STEP.DEAD);
            return;
        }

        (async () => {
            const response = await redeemBreakGlassLink(token);
            if (!response?.status) {
                setError(response?.info || "This sign-in link is no longer valid.");
                setStep(STEP.DEAD);
                return;
            }

            setChallenge(response.challenge);
            setEmail(response.email || "");

            // Branch on what the backend says, never on local state: whether this
            // is the first emergency sign-in is a server-side fact.
            if (response.next === "totp_enroll") {
                await beginEnrollment(response.challenge);
            } else {
                setStep(STEP.VERIFY);
            }
        })();
    }, [token, beginEnrollment]);

    async function submitCode() {
        if (code.length !== 6 || loading) return;

        setLoading(true);
        setError("");

        const response =
            step === STEP.ENROLL
                ? await confirmBreakGlassTotp(challenge, code)
                : await verifyBreakGlassTotp(challenge, code);

        setLoading(false);

        if (response?.status) {
            completeLogin(response);
            return;
        }

        // A wrong code is recoverable in place; everything else ends the flow and
        // sends them back to request a fresh link.
        if (response?.code === "incorrect_code" && response?.attempts_remaining > 0) {
            setError(
                `Incorrect code. ${response.attempts_remaining} ${
                    response.attempts_remaining === 1 ? "attempt" : "attempts"
                } remaining.`,
            );
            return;
        }

        setError(response?.info || "Unable to continue with emergency access.");
        setStep(STEP.DEAD);
    }

    // render
    if (step === STEP.REDEEMING) {
        return (
            <Card>
                <Stack align="center" gap={3} p={4}>
                    <LoginLogo />
                    <CircularProgress size={28} />
                    <Typography>Checking your sign-in link…</Typography>
                </Stack>
            </Card>
        );
    }

    if (step === STEP.DEAD) {
        return (
            <Card>
                <Stack align="center" gap={3} p={4}>
                    <LoginLogo />
                    <Typography variant="h6">Emergency access</Typography>
                    <Typography className={styles.error}>{error}</Typography>
                    <Button fullWidth onClick={() => navigate("/")}>
                        Back to sign in
                    </Button>
                </Stack>
            </Card>
        );
    }

    const enrolling = step === STEP.ENROLL;

    return (
        <Card>
            <Stack gap={3} p={4}>
                <Stack align="center" gap={2}>
                    <LoginLogo />
                    <Typography variant="h6">
                        {enrolling ? "Set up your authenticator" : "Emergency access"}
                    </Typography>
                    {email ? (
                        <Typography className={styles.intro}>
                            Signing in as <span className={styles.email}>{email}</span>
                        </Typography>
                    ) : null}
                </Stack>

                {enrolling && enrollment ? (
                    <Stack gap={2}>
                        <Typography className={styles.intro}>
                            Scan this with your authenticator app, then enter the 6-digit
                            code it shows.
                        </Typography>

                        <div className={styles.qr}>
                            <QRCodeSVG value={enrollment.provisioningUri} size={168} />
                        </div>

                        {/* Kept beside the QR on purpose: it is the fallback when no
                            camera is available, and the only path for a screen reader. */}
                        <Typography variant="caption">
                            Or enter this key manually:
                        </Typography>
                        <div className={styles.secret}>{enrollment.secret}</div>
                    </Stack>
                ) : (
                    <Typography className={styles.intro}>
                        Enter the 6-digit code from your authenticator app.
                    </Typography>
                )}

                <OtpInput onOtpChange={setCode} />

                {error ? (
                    <Typography className={styles.error} role="alert">
                        {error}
                    </Typography>
                ) : null}

                <Button
                    fullWidth
                    disabled={code.length !== 6 || loading}
                    onClick={submitCode}
                    endIcon={loading ? <CircularProgress size={18} /> : null}
                >
                    {enrolling ? "Confirm and sign in" : "Sign in"}
                </Button>
            </Stack>
        </Card>
    );
}
